"use client";

import { Copy, FileJson, FileText, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import type { UIMessage } from "ai";

interface ChatShareMenuProps {
	messages: UIMessage[];
	title: string;
}

function extractText(message: UIMessage): string {
	return message.parts
		.filter((p) => p.type === "text")
		.map((p) => (p as { text: string }).text)
		.join("\n\n")
		.trim();
}

function messagesToMarkdown(title: string, messages: UIMessage[]): string {
	const lines: string[] = [`# ${title || "Conversation"}`, ""];
	for (const message of messages) {
		const text = extractText(message);
		if (!text) {
			continue;
		}
		const heading =
			message.role === "user"
				? "## You"
				: message.role === "assistant"
					? "## Assistant"
					: "## System";
		lines.push(heading, "", text, "");
	}
	return lines.join("\n");
}

function download(filename: string, content: string, mime: string) {
	const blob = new Blob([content], { type: mime });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function safeName(title: string): string {
	const base = (title || "conversation")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "")
		.slice(0, 60);
	return base || "conversation";
}

export function ChatShareMenu({ messages, title }: ChatShareMenuProps) {
	const hasMessages = messages.some((m) => extractText(m).length > 0);

	const copyMarkdown = async () => {
		try {
			await navigator.clipboard.writeText(messagesToMarkdown(title, messages));
			toast.success("Copied conversation as Markdown");
		} catch {
			toast.error("Failed to copy");
		}
	};

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			toast.success("Link copied");
		} catch {
			toast.error("Failed to copy link");
		}
	};

	const downloadMarkdown = () => {
		download(
			`${safeName(title)}.md`,
			messagesToMarkdown(title, messages),
			"text/markdown",
		);
	};

	const downloadJson = () => {
		const payload = {
			title: title || "Conversation",
			messages: messages
				.map((m) => ({ role: m.role, content: extractText(m) }))
				.filter((m) => m.content.length > 0),
		};
		download(
			`${safeName(title)}.json`,
			JSON.stringify(payload, null, 2),
			"application/json",
		);
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="h-8 gap-1.5"
					disabled={!hasMessages}
					aria-label="Share or export conversation"
				>
					<Share2 className="h-4 w-4" />
					<span className="hidden sm:inline">Share</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-52">
				<DropdownMenuLabel>Share</DropdownMenuLabel>
				<DropdownMenuItem onClick={copyLink} className="gap-2">
					<Link2 className="h-4 w-4" />
					Copy link
				</DropdownMenuItem>
				<DropdownMenuItem onClick={copyMarkdown} className="gap-2">
					<Copy className="h-4 w-4" />
					Copy as Markdown
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuLabel>Export</DropdownMenuLabel>
				<DropdownMenuItem onClick={downloadMarkdown} className="gap-2">
					<FileText className="h-4 w-4" />
					Download .md
				</DropdownMenuItem>
				<DropdownMenuItem onClick={downloadJson} className="gap-2">
					<FileJson className="h-4 w-4" />
					Download .json
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
